import { FlatfileRecord } from '@flatfile/hooks';
import { DATETIME_DESCRIPTION, FIELD_TYPE, submitAction } from 'components/imports/blueprintUtils';
import {
    MAX_STR_LENGTH,
    MAX_TXT_LENGTH,
    base,
    fixAddress,
    nullable,
    required,
} from 'components/imports/schemaUtils';
import { ImportStrategy } from 'components/imports/types';
import { DEDICATION_TYPES } from 'constants/dedications';
import { IMPORT_TRANSACTIONS_LINK } from 'constants/links';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useTimezones } from 'customHooks/useTimezones';
import { isUSA } from 'helpers/address';
import { yup } from 'helpers/yup';
import { capitalize } from 'lodash';
import { ValidationError } from 'yup';

const transactionSchema = yup.object().shape({
    campaign_code: base
        .label('Campaign Code')
        .length(6)
        .alphanum()
        .when('$hasTitle', ([hasTitle], schema) =>
            hasTitle
                ? schema.nullable()
                : schema.required('Must provide a Campaign Code or Campaign Title.'),
        ),
    campaign_title: base
        .label('Campaign Title')
        .when('$hasCode', ([hasCode], schema) =>
            hasCode
                ? schema.nullable()
                : schema.required('Must provide a Campaign Code or Campaign Title.'),
        ),
    campaign_team_id: yup.number().label('Campaign Team ID').nullable().min(0),
    team_member_id: yup.number().label('Team Member ID').nullable().min(0),
    first_name: base
        .label('First Name')
        .max(MAX_STR_LENGTH)
        .when('$hasContactId', ([hasContactId], schema) =>
            hasContactId
                ? schema.nullable()
                : schema.required('Must provide First Name when no Contact ID is provided.'),
        ),
    last_name: base
        .label('Last Name')
        .max(MAX_STR_LENGTH)
        .when('$hasContactId', ([hasContactId], schema) =>
            hasContactId
                ? schema.nullable()
                : schema.required('Must provide Last Name when no Contact ID is provided.'),
        ),
    amount: required
        .label('Amount')
        .when('$isInKind', ([isInKind], schema) =>
            schema.asNumber(schema =>
                isInKind
                    ? schema.min(0)
                    : schema.positive(
                          'Zero dollar donations are only allowed for the following payment method types: in-kind.',
                      ),
            ),
        ),
    platform_fee: nullable.label('Platform Fee').asNumber(schema => schema.nullable().min(0)),
    processing_fee: nullable.label('Processing Fee').asNumber(schema => schema.nullable().min(0)),
    fee_covered: nullable.label('Fee Covered').asNumber(schema => schema.nullable().min(0)),
    fund_code: nullable.label('Fund Code').max(MAX_STR_LENGTH),
    method: required
        .label('Payment Method')
        .oneOf([
            'ach',
            'card',
            'cash',
            'check',
            'digital_wallet',
            'paypal',
            'venmo',
            'donor_advised_fund',
            'in-kind',
            'stock',
            'property',
            'other',
        ]),
    transacted_at: required.label('Transaction Date').date({ before: true }),
    external_label: nullable.label('External Label').max(MAX_STR_LENGTH),
    external_id: nullable.label('External ID').max(MAX_STR_LENGTH),
    acknowledged_at: nullable.label('Acknowledged Date').date({ before: true }),
    check_number: nullable.label('Check Number').max(MAX_STR_LENGTH),
    check_deposited_at: nullable.label('Check Deposit Date').date({ before: true }),
    contact_id: yup.number().label('Contact ID').nullable().min(0),
    company: nullable.label('Company').max(MAX_STR_LENGTH),
    email: nullable.label('Email').email(),
    phone: nullable.label('Phone').max(MAX_STR_LENGTH),
    address_1: base
        .label('Address Line 1')
        .max(MAX_STR_LENGTH)
        .when('$hasPartialAddress', ([hasPartialAddress], schema) =>
            hasPartialAddress
                ? schema.required('${label} is required for a valid address.')
                : schema.nullable(),
        ),
    address_2: nullable.label('Address Line 2').max(MAX_STR_LENGTH),
    city: base
        .label('City')
        .max(MAX_STR_LENGTH)
        .when('$hasPartialAddress', ([hasPartialAddress], schema) =>
            hasPartialAddress
                ? schema.required('${label} is required for a valid address.')
                : schema.nullable(),
        ),
    state: nullable.label('State').max(MAX_STR_LENGTH).state(),
    zipcode: nullable
        .label('Postal Code')
        .max(MAX_STR_LENGTH)
        .max(MAX_STR_LENGTH)
        .when('country', {
            is: (country: string | null) => isUSA(country),
            then: schema => schema.postalCode(),
        })
        .when('$hasPartialAddress', ([hasPartialAddress], schema) =>
            hasPartialAddress
                ? schema.required('${label} is required for a valid address.')
                : schema.nullable(),
        ),
    country: nullable.label('Country').max(MAX_STR_LENGTH).country(),
    internal_note: nullable.max(MAX_STR_LENGTH).label('Internal Note'),
    dedication_type: nullable
        .label('Dedication Type')
        .oneOf(DEDICATION_TYPES)
        .when('$hasPartialDedication', ([hasPartialDedication], schema) =>
            hasPartialDedication
                ? schema.required('${label} is required for a valid dedication.')
                : schema.nullable(),
        ),
    dedication_name: nullable
        .label('Dedication Honoree Name')
        .max(MAX_STR_LENGTH)
        .when('$hasPartialDedication', ([hasPartialDedication], schema) =>
            hasPartialDedication
                ? schema.required('${label} is required for a valid dedication.')
                : schema.nullable(),
        ),
    dedication_recipient_name: nullable.label('Dedication Recipient Name').max(MAX_STR_LENGTH),
    dedication_recipient_email: nullable.label('Dedication Recipient Email').email(),
    giving_space_message: nullable.label('Thank You Message').max(MAX_TXT_LENGTH),
});

const useTransactionImportStrategy = (): ImportStrategy => {
    const timezones = useTimezones();
    const { timezone: accountTz } = useCurrentAccount();

    const schema = transactionSchema.concat(
        yup.object().shape({
            timezone: nullable.label('Time zone').oneOf(
                timezones.map(tz => tz.name),
                'Must provide a valid time zone',
            ),
        }),
    );

    return {
        displayName: 'transactions',
        type: 'transactions',
        hasDownloadTemplate: false,
        helpCenterUrl: IMPORT_TRANSACTIONS_LINK,
        blueprint: {
            name: 'Import transactions',
            sheets: [
                {
                    name: 'Transactions',
                    description: 'Migrate transactions from another platform.',
                    slug: 'transactions',
                    readonly: false,
                    fields: [
                        {
                            label: 'Campaign Code',
                            key: 'campaign_code',
                            type: FIELD_TYPE.string,
                            description:
                                "The code that identifies the campaign that should be associated with the transaction. Found on a campaign's details page.",
                        },
                        {
                            label: 'Campaign Title',
                            key: 'campaign_title',
                            type: FIELD_TYPE.string,
                            description: `
                            The title of the campaign that should be associated with the transaction.
                                * If a campaign with that title is not found, a new one will be created as an external campaign. External campaigns are only available for record keeping.
                                * Will be ignored if a campaign code is provided.
                                `,
                        },
                        {
                            label: 'Campaign Team ID',
                            key: 'campaign_team_id',
                            type: FIELD_TYPE.number,
                            description:
                                'The ID of the campaign team you wish to associate with the transaction.',
                        },
                        {
                            label: 'Team Member ID',
                            key: 'team_member_id',
                            type: FIELD_TYPE.number,
                            description:
                                'The ID of the team member you wish to associate with the transaction.',
                        },
                        {
                            label: 'First Name',
                            key: 'first_name',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Last Name',
                            key: 'last_name',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Amount ($)',
                            key: 'amount',
                            type: FIELD_TYPE.number,
                            description:
                                'The amount given by the donor. *Does not include fees covered.*',
                        },
                        {
                            label: 'Fund Code',
                            key: 'fund_code',
                            type: FIELD_TYPE.string,
                            description:
                                'The code of the fund that the transaction should be designated to. Found under *Account* &gt; *Settings* &gt; *Funds*.',
                        },
                        {
                            label: 'Payment Method',
                            key: 'method',
                            type: FIELD_TYPE.enum,
                            config: {
                                options: [
                                    { value: 'ach', label: 'ACH' },
                                    { value: 'card', label: 'Card' },
                                    { value: 'cash', label: 'Cash' },
                                    { value: 'check', label: 'Check' },
                                    { value: 'digital_wallet', label: 'Digital Wallet' },
                                    { value: 'paypal', label: 'PayPal' },
                                    { value: 'venmo', label: 'Venmo' },
                                    { value: 'donor_advised_fund', label: 'Donor Advised Fund' },
                                    { value: 'in-kind', label: 'In kind' },
                                    { value: 'stock', label: 'Stock' },
                                    { value: 'property', label: 'Property' },
                                    { value: 'other', label: 'Other' },
                                ],
                            },
                        },
                        {
                            label: 'Transaction Date',
                            key: 'transacted_at',
                            type: FIELD_TYPE.string,
                            description: DATETIME_DESCRIPTION,
                        },
                        {
                            label: 'Time zone',
                            key: 'timezone',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Platform Fee ($)',
                            key: 'platform_fee',
                            type: FIELD_TYPE.number,
                            description: 'The fee charged by the platform.',
                        },
                        {
                            label: 'Processing Fee ($)',
                            key: 'processing_fee',
                            type: FIELD_TYPE.number,
                            description: 'The fee charged by the processing agent.',
                        },
                        {
                            label: 'Fee Covered ($)',
                            key: 'fee_covered',
                            type: FIELD_TYPE.number,
                            description:
                                'The amount of fees covered by the donor. Must be less than `Platform Fee` + `Processing Fee`.',
                        },
                        {
                            label: 'External Label',
                            key: 'external_label',
                            type: FIELD_TYPE.string,
                            description:
                                'A label for the platform the transaction is being imported from.',
                        },
                        {
                            label: 'External ID',
                            key: 'external_id',
                            type: FIELD_TYPE.string,
                            description: 'The ID of the transaction used by the previous platform.',
                        },
                        {
                            label: 'Acknowledged Date',
                            key: 'acknowledged_at',
                            type: FIELD_TYPE.string,
                            description: DATETIME_DESCRIPTION,
                        },
                        {
                            label: 'Check Number',
                            key: 'check_number',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Check Deposit Date',
                            key: 'check_deposited_at',
                            type: FIELD_TYPE.string,
                            description: DATETIME_DESCRIPTION,
                        },
                        {
                            label: 'Contact ID',
                            key: 'contact_id',
                            type: FIELD_TYPE.number,
                        },
                        {
                            label: 'Company',
                            key: 'company',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Email',
                            key: 'email',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Phone',
                            key: 'phone',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Address Line 1',
                            key: 'address_1',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Address Line 2',
                            key: 'address_2',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'City',
                            key: 'city',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'State',
                            key: 'state',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Postal Code',
                            key: 'zipcode',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Country',
                            key: 'country',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Internal Note',
                            key: 'internal_note',
                            type: FIELD_TYPE.string,
                        },
                        {
                            key: 'dedication_type',
                            label: 'Dedication Type',
                            type: FIELD_TYPE.enum,
                            config: {
                                options: DEDICATION_TYPES.map(type => ({
                                    value: type,
                                    label: capitalize(type.replaceAll('_', ' ')),
                                })),
                            },
                        },
                        {
                            key: 'dedication_name',
                            label: 'Dedication Honoree Name',
                            type: FIELD_TYPE.string,
                        },
                        {
                            key: 'dedication_recipient_name',
                            label: 'Dedication Recipient Name',
                            type: FIELD_TYPE.string,
                        },
                        {
                            key: 'dedication_recipient_email',
                            label: 'Dedication Recipient email',
                            type: FIELD_TYPE.string,
                        },
                        {
                            key: 'giving_space_message',
                            label: 'Thank You Message',
                            type: FIELD_TYPE.string,
                            description:
                                'Message will appear in the supporter feed if the campaign is active.',
                        },
                    ],
                },
            ],
            actions: [submitAction],
        },
        recordHook: (record: FlatfileRecord) => {
            const data = record.value;

            fixAddress(record);

            const context = {
                hasCode: data.campaign_code !== null,
                hasTitle: data.campaign_title !== null,
                hasContactId: data.contact_id !== null,
                hasPartialAddress:
                    data.address_1 !== null ||
                    data.address_2 !== null ||
                    data.city !== null ||
                    data.state !== null ||
                    data.zipcode !== null ||
                    data.country !== null,
                hasPartialDedication:
                    data.dedication_type !== null ||
                    data.dedication_name !== null ||
                    data.dedication_recipient_name !== null ||
                    data.dedication_recipient_email !== null,
                isInKind: data.method === 'in-kind',
            };

            try {
                schema.validateSync(data, { context, abortEarly: false });
            } catch (err) {
                if (!(err instanceof ValidationError)) {
                    return record;
                }

                if (!err.inner) {
                    record.addError(err.path!, err.message);

                    return record;
                }

                err.inner.forEach((e: any) => {
                    record.addError(e.path, e.message);
                });
            }

            const externalFields = [data.external_id, data.external_label];
            if (externalFields.filter(field => field === null).length === 1) {
                record.addError(
                    externalFields[0] === null ? 'external_id' : 'external_label',
                    'External ID and External Label must be provided together.',
                );
            }

            if (data.timezone === null || data.timezone === '') {
                record.addWarning(
                    'timezone',
                    `Time zone not provided — will default to account time zone: ${accountTz}`,
                );
            }

            if (data.campaign_code !== null && data.campaign_title !== null) {
                record.addWarning(
                    'campaign_title',
                    'Campaign Title will not be used when Campaign Code is provided.',
                );
            }

            return record;
        },
    };
};

export { useTransactionImportStrategy };
